// Runtime configuration. In production the web container writes /env.js at start (nginx
// entrypoint envsubst) which sets window.__HF_ENV — so ONE image works across
// environments (EasyPanel builds don't see build-time VITE_* vars). In local dev there is
// no /env.js, so we fall back to Vite's import.meta.env (.env file).

interface HfEnv {
  SUPABASE_URL?: string;
  SUPABASE_ANON_KEY?: string;
  AUTH_PROVIDERS?: string; // comma-separated: e.g. "google,azure,oidc"
  GIT_SHA?: string; // the commit the web container was deployed from (EasyPanel)
  DEPLOY_TIMESTAMP?: string; // ms since epoch, EasyPanel's deploy time
}

declare global {
  interface Window {
    __HF_ENV?: HfEnv;
  }
}

const injected = (typeof window !== 'undefined' && window.__HF_ENV) || {};

function pick(runtime: string | undefined, viteVal: string | undefined): string {
  // Ignore un-substituted placeholders (e.g. "${SUPABASE_URL}") when a container runs
  // without the var set.
  const r = runtime && !runtime.startsWith('${') ? runtime : '';
  return r || viteVal || '';
}

export const runtimeConfig = {
  supabaseUrl: pick(injected.SUPABASE_URL, import.meta.env.VITE_SUPABASE_URL as string | undefined),
  supabaseAnonKey: pick(
    injected.SUPABASE_ANON_KEY,
    import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined,
  ),
  // Which OAuth/OIDC providers to show. Defaults to none (email/password + magic link
  // always available). e.g. "google,azure,oidc".
  authProviders: pick(injected.AUTH_PROVIDERS, import.meta.env.VITE_AUTH_PROVIDERS as string | undefined)
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean),
  // The build the SERVER currently serves (written into /env.js at container start). Compared
  // with the sha baked into this bundle to detect a stale tab — see engine/shell/StaleBuildBanner.
  servedSha: pick(injected.GIT_SHA, undefined) || 'unknown',
  deployedAt: ((): string | null => {
    const n = Number(pick(injected.DEPLOY_TIMESTAMP, undefined));
    return Number.isFinite(n) && n > 0 ? new Date(n).toISOString() : null;
  })(),
};

/** True when Supabase auth is configured (URL + anon key present). */
export const isAuthConfigured = Boolean(runtimeConfig.supabaseUrl && runtimeConfig.supabaseAnonKey);
